import { useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, CreditCard, Info, Loader2 } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { usePaymentPlanQuery } from '@/lib/builder/hooks/usePaymentPlan';
import { useCreateItineraryPaymentLink } from '@/lib/builder/hooks/useItineraryPayment';
import { PaymentPlanSummary } from '@/lib/builder/components/payment-plan/PaymentPlanSummary';
import { InstallmentRow } from '@/lib/builder/components/payment-plan/InstallmentRow';
import {
    firstPayableInstallment,
    hasMultipleInstallments,
    hasPaymentInFlight,
    summarizePlan,
} from '@/lib/builder/utils/payment-plan.utils';
import { formatPlanAmount, formatPlanDate } from '@/lib/builder/utils/payment-format';
import { PaymentProcessingNotice } from '@/lib/builder/components/payment-plan/PaymentProcessingNotice';
import { useAuth } from '@/hooks/useAuth';
import { useItineraryLegalSettings } from '@/lib/legal/hooks/useItineraryLegalSettings';
import { LegalAcceptance } from '@/lib/legal/LegalAcceptance';
import { hasLegalDocuments } from '@/lib/legal/legal.model';
import { useItineraryPaymentOptions } from '@/lib/payments/hooks/usePaymentOptions';
import { PaymentMethodChoiceModal } from '@/lib/payments/components/PaymentMethodChoiceModal';
import { isBankTransferAvailable } from '@/lib/payments/models/payment-options.model';

interface ItineraryPaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    itineraryId: string;
    /** Totale dell'itinerario: l'unico importo disponibile quando non c'e' un piano rate. */
    itineraryTotal?: number;
    /** true quando l'itinerario risulta gia' saldato (status 'completed'). */
    isSettled: boolean;
}

/**
 * Il pagamento visto dal cliente autenticato: cosa ha gia' versato, cosa resta, e il
 * pulsante per saldare il prossimo importo dovuto.
 *
 * E' la stessa informazione che il TD vede nella PaymentPlanCard del checkout, ripulita
 * di tutto cio' che e' configurazione (promemoria, editor, incasso manuale). Il cliente
 * finora vedeva solo il totale e un pulsante "Book your trip" che chiedeva l'intera cifra
 * anche quando l'advisor aveva definito un acconto.
 *
 * L'importo non viene mai deciso qui: si manda l'id della rata alla edge function, che
 * rilegge la cifra dal database.
 */
export const ItineraryPaymentModal = ({
    isOpen,
    onClose,
    itineraryId,
    itineraryTotal,
    isSettled,
}: ItineraryPaymentModalProps) => {
    const { t, language } = useI18n();
    const { userRole } = useAuth();

    // La spunta riguarda chi paga per se' stesso. Questa stessa modale la apre anche
    // l'advisor dalla dashboard pagamenti per generare il link al cliente: li' non c'e'
    // nessun consenso da raccogliere, e il server infatti non lo pretende.
    const isTraveler = userRole === 'traveler';
    const { legal } = useItineraryLegalSettings(itineraryId, isOpen && isTraveler);
    const requiresLegalAcceptance = isTraveler && hasLegalDocuments(legal);
    const [legalAccepted, setLegalAccepted] = useState(false);
    const canPay = !requiresLegalAcceptance || legalAccepted;

    // La queryKey NON deve dipendere da isOpen: la modale resta montata per i 200 ms
    // dell'animazione di uscita, e passare a una chiave diversa (vuota) alla chiusura
    // faceva sparire il piano a meta' transizione — la finestra si rimpiccioliva da 'lg'
    // a 'sm' collassando verso il centro. La query e' comunque gia' montata nel footer,
    // stessa chiave: qui non costa nulla.
    const { data: plan, isLoading, isError } = usePaymentPlanQuery(itineraryId);
    const createLink = useCreateItineraryPaymentLink();

    // Metodi scelti dal TA. Con il bonifico il "Paga" apre la scelta invece di Stripe;
    // senza configurazione vale solo Stripe, come prima.
    const { options: paymentOptions } = useItineraryPaymentOptions(itineraryId, isOpen);
    const bankAvailable = isBankTransferAvailable(paymentOptions);
    const stripeAllowed = paymentOptions?.methods.includes('stripe') ?? true;
    const [choiceInstallmentId, setChoiceInstallmentId] = useState<string | null | undefined>(undefined);

    const summary = summarizePlan(plan);
    const hasInstallments = hasMultipleInstallments(plan);
    const nextInstallment = firstPayableInstallment(plan);
    /**
     * Tentativo di pagamento aperto e non ancora incassato. NON spegne la CTA: la riga
     * 'pending' nasce dal semplice arrivo del cliente sul bonifico in Checkout, quindi
     * spegnerla lasciava senza uscita chi aveva aperto il checkout e non aveva concluso.
     * Vale un avviso sopra il riepilogo, niente di piu'.
     */
    const paymentInFlight = hasPaymentInFlight(plan);

    const formatAmount = (value: number) => formatPlanAmount(value);
    const formatDate = (iso: string) => formatPlanDate(iso, language);

    const isPaying = createLink.isPending;

    const payTotal = () => createLink.mutate({ itineraryId, legalAccepted });
    const payInstallment = (installmentId: string) =>
        createLink.mutate({ itineraryId, installmentId, legalAccepted });

    // Col piano rate comanda il piano: lo status 'completed' arriva gia' al primo acconto.
    const settled = summary ? summary.allPaid : isSettled;

    /**
     * La CTA del footer. Con le rate serve una rata aperta: se il piano esiste ma sono
     * tutte pagate o annullate non c'e' niente da pagare, e un pulsante che manda al
     * totale incasserebbe una seconda volta.
     */
    const showPayAction = !settled && !isError && (!hasInstallments || !!nextInstallment);
    const payLabel =
        hasInstallments && nextInstallment
            ? t(
                  nextInstallment.payment_processing
                      ? 'itinerary.payment.retryInstallmentLabel'
                      : 'itinerary.payment.payInstallmentLabel',
                  { label: nextInstallment.label },
              )
            : t('itinerary.payment.payAmount', {
                  amount: formatAmount(plan?.total_amount ?? itineraryTotal ?? 0),
              });
    const payNextOnStripe = () =>
        hasInstallments && nextInstallment ? payInstallment(nextInstallment.id) : payTotal();
    const payNext = () => {
        if (bankAvailable) {
            setChoiceInstallmentId(hasInstallments && nextInstallment ? nextInstallment.id : null);
            return;
        }
        payNextOnStripe();
    };

    // Importo e causale proposti per il bonifico.
    const choiceInstallment = choiceInstallmentId
        ? plan?.installments.find((i) => i.id === choiceInstallmentId)
        : firstPayableInstallment(plan);
    const choiceAmount = choiceInstallment?.amount ?? plan?.total_amount ?? itineraryTotal ?? 0;

    return (
        <>
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('itinerary.payment.title')}
            size={hasInstallments ? 'lg' : 'sm'}
            footer={
                /* Il padding del footer lo mette gia' la Modal: qui raddoppiava.
                   La spunta sta sopra la riga dei pulsanti e non in mezzo al contenuto:
                   e' l'ultima cosa che il cliente legge prima di cliccare, e resta
                   visibile anche quando l'elenco delle rate scorre. */
                <div className="flex flex-1 flex-col gap-3">
                    {showPayAction && requiresLegalAcceptance && (
                        <LegalAcceptance
                            legal={legal}
                            checked={legalAccepted}
                            onCheckedChange={setLegalAccepted}
                        />
                    )}

                    <div className="flex items-center justify-between gap-2">
                        <Button variant="ghost" size="sm" onClick={onClose}>
                            {t('common.close')}
                        </Button>

                        {/* Una sola CTA, sempre nello stesso posto: paga il totale quando non
                            c'e' un piano, altrimenti la prima rata ancora aperta — che e' l'unica
                            pagabile. Il testo nomina la rata, cosi' si capisce cosa si sta saldando
                            anche se il pulsante non e' piu' sulla sua riga. */}
                        {showPayAction && (
                            <Button
                                size="sm"
                                onClick={payNext}
                                disabled={isPaying || isLoading || !canPay}
                                className="flex items-center gap-2"
                            >
                                {isPaying ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <CreditCard className="h-4 w-4" />
                                )}
                                {payLabel}
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-3 px-6 py-4">
                {isLoading ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('common.loading')}
                    </div>
                ) : isError ? (
                    <Alert variant="destructive">
                        <AlertDescription>{t('itinerary.payment.errors.generic')}</AlertDescription>
                    </Alert>
                ) : settled ? (
                    <div className="flex items-center gap-3 rounded-lg bg-green-50 p-4 text-green-800">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                        <div className="flex flex-col">
                            <span className="font-medium">{t('itinerary.payment.settledTitle')}</span>
                            <span className="text-sm">{t('itinerary.payment.settledDescription')}</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Un tentativo aperto non impedisce di ripagare, ma il cliente e' l'unico
                            che sa se il bonifico l'ha davvero disposto: va detto prima del click. */}
                        {paymentInFlight && <PaymentProcessingNotice />}
                        {hasInstallments ? (
                    <>
                        <PaymentPlanSummary plan={plan!} formatAmount={formatAmount} />

                        {nextInstallment && (
                            <Alert variant="info">
                                <Info className="h-4 w-4" />
                                <AlertDescription>
                                    {t('itinerary.payment.nextInstallmentHint', {
                                        label: nextInstallment.label,
                                        date: formatDate(nextInstallment.due_date),
                                    })}
                                </AlertDescription>
                            </Alert>
                        )}

                        {/* Scroll sull'elenco e non sulla modale: il riepilogo e l'avviso
                            sulla prossima rata restano visibili anche con molte rate. */}
                        <div className="flex flex-col divide-y divide-gray-100 max-h-[min(45vh,20rem)] overflow-y-auto -mr-1 pr-1">
                            {plan!.installments.map((installment) => (
                                <InstallmentRow
                                    key={installment.id}
                                    installment={installment}
                                    isPayable={canPay && installment.id === nextInstallment?.id}
                                    isPaying={isPaying}
                                    onPay={payInstallment}
                                    formatAmount={formatAmount}
                                    formatDate={formatDate}
                                    /* La CTA e' nel footer: qui la riga resta di sola lettura. */
                                    showPayAction={false}
                                />
                            ))}
                        </div>
                    </>
                        ) : (
                            <div className="flex flex-col gap-3">
                                <div className="flex items-baseline justify-between gap-4 rounded-lg bg-gray-50 p-4">
                                    <span className="text-gray-600">{t('itinerary.payment.amountDue')}</span>
                                    <span className="text-xl font-semibold text-gray-900 tabular-nums">
                                        {formatAmount(plan?.total_amount ?? itineraryTotal ?? 0)}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500">{t('itinerary.payment.singleDescription')}</p>
                                <p className="text-xs text-gray-500">
                                    {t('itinerary.payment.bankTransferNotice')}
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* total_mismatch: il preventivo e' cambiato dopo che il piano e' stato
                    definito. Le rate non coprono piu' il totale, e il cliente non deve
                    scoprirlo pagando. */}
                {plan?.total_mismatch && !settled && (
                    <Alert variant="destructive">
                        <AlertDescription>{t('itinerary.payment.totalMismatch')}</AlertDescription>
                    </Alert>
                )}
            </div>
        </Modal>

        {paymentOptions && (
            <PaymentMethodChoiceModal
                isOpen={isOpen && choiceInstallmentId !== undefined}
                onClose={() => setChoiceInstallmentId(undefined)}
                options={paymentOptions}
                stripeAvailable={stripeAllowed}
                onPayOnline={payNextOnStripe}
                isPayingOnline={isPaying}
                amountLabel={formatAmount(choiceAmount)}
                fallbackReference={choiceInstallment?.label ?? ''}
            />
        )}
        </>
    );
};